import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmDialog, Text } from "@marekl/adminer/components";
import { useLogout } from "./queries";
import { onSessionExpired } from "./sessionExpiry";

/**
 * Tells the user their session ended, instead of letting the app fail
 * silently.
 *
 * It deliberately does **not** redirect on its own. The page underneath
 * may hold work that exists nowhere else — the season registration draft
 * is the obvious one — and throwing it away to show a login form turns a
 * recoverable annoyance into lost data. So the modal states what
 * happened, says the last request did not go through, and leaves the
 * choice: log in again (which does discard the page), or stay and copy
 * something out first.
 *
 * Mounted inside the authenticated shell only, which is what keeps a
 * rejected login on the public login page from ever reaching it.
 */
export function SessionExpiredModal() {
  const [open, setOpen] = useState(false);
  const { mutate: logout, isPending } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => onSessionExpired(() => setOpen(true)), []);

  function relogin() {
    const returnTo = location.pathname + location.search;
    // Clears the cached user even if the logout call itself 401s, so the
    // login page does not bounce us straight back to the dashboard.
    logout(undefined, {
      onSettled: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true }),
    });
  }

  return (
    // ConfirmDialog rather than useConfirm: this opens from an event, not from
    // something the user pressed, so there is no call site to hand a promise
    // back to.
    <ConfirmDialog
      open={open}
      title="Přihlášení vypršelo"
      width={460}
      cancelLabel="Zůstat na stránce"
      confirmLabel="Přihlásit se znovu"
      busy={isPending}
      onClose={() => setOpen(false)}
      onConfirm={relogin}
    >
      <Text as="p">
        Server odmítl poslední požadavek; vaše přihlášení vypršelo. Co jste právě
        odeslali, se neuložilo.
      </Text>
      <Text as="p" variant="muted">
        Rozpracované změny na této stránce zatím zůstávají. Přihlášením se stránka
        načte znovu a změny se ztratí. Pokud si je potřebujete opsat, zvolte
        „Zůstat na stránce“.
      </Text>
    </ConfirmDialog>
  );
}
